import React, { useState } from 'react';
import { MessageCircle, Clock, Heart, Wrench } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { MicroLabel } from './micro-label';

const benefits = [
  {
    icon: MessageCircle,
    title: "We speak human",
    description: "Clear communication without technical jargon.",
    detail: "Weekly updates in business terms you understand, with honest timelines and transparent progress tracking."
  },
  {
    icon: Clock,
    title: "Predictable delivery", 
    description: "On-time launches with measurable outcomes.",
    detail: "Your project launches on schedule with clear metrics showing real business impact and ROI."
  },
  {
    icon: Heart,
    title: "Post-launch partnership",
    description: "Ongoing support beyond project completion.",
    detail: "We help your solution evolve and perform better over time with dedicated support and optimization."
  },
  {
    icon: Wrench,
    title: "Business outcomes first",
    description: "Solutions designed for your specific goals.",
    detail: "Every project is tailored to your customers and business objectives, not just technical requirements."
  }
];

export const WhyBusinessesChooseUs = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <section className="section-spacing bg-surface--1 relative overflow-hidden">
      <div ref={elementRef} className="container mx-auto px-6 relative z-10">
        <div className="mb-16 max-w-content mx-auto">
          <MicroLabel>PRINCIPLES</MicroLabel>
          <h2 className="font-display font-semibold text-foreground title-spacing text-left" style={{ fontSize: '40px', lineHeight: '1.15', letterSpacing: '-0.25px' }}>
            What you'll feel working with us
          </h2>
          
          {/* One-liner strip */}
          <div className="trust-line mb-8 flex flex-wrap gap-4">
            <span>Clear comms</span>
            <span>—</span>
            <span>Predictable delivery</span>
            <span>—</span>
            <span>Honest estimates</span>
            <span>—</span>
            <span>Post-launch care</span>
          </div>
        </div>

        <div ref={elementRef} className="max-w-content mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mobile-card-group">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              const isHovered = hoveredCard === index;
              
              // Signature card variants
              const cardVariant = index % 3 === 0 ? 'card-signature-bar' : 'card-studio';
              
              return (
                <div 
                  key={index}
                  className={`${cardVariant} cursor-pointer group`}
                  style={getRevealStyle(index)}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="p-5 md:p-7 mobile-tight h-full">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className="w-4 h-4 md:w-5 md:h-5 text-primary flex-shrink-0 mt-1">
                        <Icon 
                          className="w-full h-full icon-hover icon-pulse" 
                          strokeWidth={1.5} 
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display font-medium text-foreground text-base md:text-lg mb-2 leading-tight">
                          {benefit.title}
                        </h3>
                        <p className="text-body text-xs md:text-sm mb-3 leading-relaxed">
                          {benefit.description}
                        </p>
                        <div className={`transition-all duration-300 md:${
                          isHovered ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
                        } max-h-32 opacity-100 md:overflow-hidden`}>
                          <p className="text-xs text-body/80 pt-2 border-t border-stroke-hairline">
                            {benefit.detail}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Section divider */}
        <div className="section-divider"></div>
      </div>
    </section>
  );
};